import { Link } from 'react-router-dom';
import {
  ShieldCheck, BadgeCheck, FileLock2, Server, Lock, Users, Eye,
  MapPin, Sparkles, ClipboardCheck, Undo2, AlertTriangle, Download,
} from 'lucide-react';
import { PageMeta } from '@/components/PageMeta';

/**
 * /security — compliance-checklist doc format.
 *
 * Every other marketing page has its own shape. /security reads like a
 * signed compliance document: sticky anchor nav on the left, numbered
 * sections on the right (§01 Certifications, §02 Data handling, ...),
 * plain-English body with concrete practices. No hero mock, no
 * testimonials, no illustrations — this page is for enterprise IT
 * procurement teams scanning for a specific standard.
 *
 * Design spec: docs/design/marketing/_per-page-design-language.md
 * (security gets the compliance-checklist doc format).
 */

interface Section {
  id: string;
  num: string;
  title: string;
  intro?: string;
  items: Array<{ icon?: React.ReactNode; label: string; body: React.ReactNode }>;
}

const SECTIONS: Section[] = [
  {
    id: 'certifications',
    num: '01',
    title: 'Certifications & attestations',
    intro:
      'The four things enterprise procurement asks about first. Every one carries an audit trail; the letters are available under NDA on request.',
    items: [
      { icon: <ShieldCheck size={16} />, label: 'SOC 2 Type II', body: <>Independently audited security controls across confidentiality, integrity and availability. Annual re-audit.</> },
      { icon: <BadgeCheck size={16} />,  label: 'ISO/IEC 27001', body: <>Certified information security management system covering our full engineering and support surface.</> },
      { icon: <FileLock2 size={16} />,   label: 'DPDP Act 2023 ready', body: <>Named DPO. Consent-captured on signup. Full export + delete flows. Version-tracked. See §04 below.</> },
      { icon: <Server size={16} />,      label: 'Hosted in India', body: <>All customer data, backups and audit logs stay in-country. One tenant per college by default. See §05.</> },
    ],
  },
  {
    id: 'data-handling',
    num: '02',
    title: 'Data handling',
    intro: 'What we collect, why we collect it, and what we never touch.',
    items: [
      { label: 'Data we collect', body: <>Student profile (name, marks, projects, resume), college roster (issued by your institution), applications and offers you create in the register. Nothing outside your placement workflow.</> },
      { label: 'Data we never collect', body: <>No third-party trackers. No advertising IDs. No location beyond the college city you tell us. No biometric data unless the assessment you\'re running explicitly enables camera proctoring (§07).</> },
      { label: 'Encryption', body: <>TLS 1.2+ in transit. AES-256 at rest. Backups encrypted with a separate key. Secrets rotated on a schedule.</> },
      { label: 'Retention', body: <>Active tenant data kept as long as your contract is live + 60 days soft-delete. Assessment attempts have shorter retentions per §07. Audit logs kept 7 years for regulatory purposes.</> },
    ],
  },
  {
    id: 'access',
    num: '03',
    title: 'Access controls',
    intro: 'Who can see what, and how the audit answers the "how do you know?" question.',
    items: [
      { icon: <Users size={16} />, label: 'Role-based permissions', body: <>Every user has a role (student / placement officer / HoD / group admin / etc). Permissions are scoped by role AND by the tenant subtree they belong to. A college admin cannot see another college in the group.</> },
      { icon: <Lock size={16} />, label: 'MFA + SSO', body: <>MFA available on every account; required for staff roles. SSO via Google Workspace + Microsoft Entra on paid tiers. SCIM provisioning on the University tier.</> },
      { icon: <Eye size={16} />, label: 'Audit log', body: <>Every mutation writes an audit-log entry — who, what, when, from where. Signed export in JSON on request. See §06 for what the audit catches.</> },
    ],
  },
  {
    id: 'dpdp',
    num: '04',
    title: 'DPDP Act 2023 — the plain-English version',
    intro:
      'Under India\'s Digital Personal Data Protection Act, 2023, students on Kollez can:',
    items: [
      { icon: <Eye size={16} />,      label: 'See what we hold', body: <>Full data-view flow from Settings → Privacy. Every field labeled with where it came from (self-entered, DigiLocker, college roster, drive result).</> },
      { icon: <Download size={16} />, label: 'Take it with them', body: <>JSON export on demand. Includes profile, applications, offers, assessment attempts, badges, and audit trail.</> },
      { icon: <AlertTriangle size={16} />, label: 'Ask us to forget them', body: <>Delete-account flow with 60 days to cancel. On expiry, personal data is scrubbed; audit-log entries are retained anonymously for regulatory purposes.</> },
      { icon: <Users size={16} />,    label: 'Know who inside their college can see what', body: <>Field-level visibility explicit in the profile UI. TPO sees marks + applications; HoD sees department roll-up; group admin sees group roll-up; nobody sees a student\'s Nexie chats or mock-interview transcripts.</> },
    ],
  },
  {
    id: 'hosting',
    num: '05',
    title: 'Hosting & residency',
    items: [
      { icon: <MapPin size={16} />, label: 'India-only residency', body: <>Primary and backup regions both in India. No cross-border data transfer for tenant records. Sub-processor list published on request.</> },
      { label: 'Tenant isolation', body: <>One tenant per college by default. Group tenants nest child colleges; permissions never cross the boundary you configure.</> },
      { label: 'Uptime & backups', body: <>99.9% target uptime. Daily encrypted backups. Point-in-time restore up to 30 days. Status page at <Link to="/status" className="link">/status</Link>.</> },
    ],
  },
  {
    id: 'sign-off',
    num: '06',
    title: 'Offer sign-off & undo',
    intro:
      'Before an offer leaves your placement cell, Kollez runs four automated checks — and every acceptance has a 10-minute undo window.',
    items: [
      { icon: <ClipboardCheck size={16} />, label: 'Automated checks', body: <>Name matches roster · CTC within policy band · Signatory authorised · Student not already placed elsewhere. Any one fails, the offer is held for human review.</> },
      { icon: <Undo2 size={16} />,          label: '10-minute undo window', body: <>Every acceptance can be reverted for 10 minutes without a support ticket. Turns a scare into a save.</> },
      { icon: <Eye size={16} />,            label: 'Full audit chain', body: <>Every check result + every reviewer decision + every timestamp is on the offer\'s audit trail. Signed export ships with the offer letter.</> },
    ],
  },
  {
    id: 'assessments',
    num: '07',
    title: 'Assessments & proctoring',
    intro:
      'Camera, tab-switch and paste-block are opt-in per assessment. Every integrity flag is human-reviewed; no auto-void.',
    items: [
      { label: 'Consent before proctoring', body: <>Student must consent per assessment before any camera or tab-switch monitoring runs. Consent version is versioned in the student\'s record.</> },
      { label: 'Seven integrity flags', body: <>answerMatch, faceMismatch, impossibleSpeed, tabSwitch, paste, codeSimilarity, networkTiming. No auto-void; every flag is reviewed by staff. 7-day appeal on any void.</> },
      { label: 'Audio/video retention', body: <>Shortest retention on the platform. Deleted per assessment policy; you can shorten further. See <Link to="/assessments" className="link">/assessments</Link> for the full posture.</> },
      { label: 'Accommodations', body: <>Extra-time percentages per candidate are respected automatically. The proctoring does not treat accommodated time as suspicious.</> },
    ],
  },
  {
    id: 'nexie',
    num: '08',
    title: 'AI (Nexie) safeguards',
    intro:
      'Nexie is our education-tuned AI. It drafts, ranks and explains. It never takes an autonomous action, and it does not train on cross-tenant data.',
    items: [
      { icon: <Sparkles size={16} />, label: 'Grounded + cited', body: <>Every Nexie answer cites the rows it drew from. If Nexie cannot find a source, it says "I don\'t know" instead of making something up.</> },
      { icon: <Users size={16} />,    label: 'Human sign-off always', body: <>Every publish, offer, shortlist or report needs a person on your team to say yes before it moves. Nexie proposes; you dispose.</> },
      { icon: <Lock size={16} />,     label: 'No cross-tenant training', body: <>Your students\' data does not train a model shared with any other tenant. Ever.</> },
      { icon: <ShieldCheck size={16} />, label: 'Fraud detection', body: <>Nexie flags fake jobs, spam applications and duplicate profiles ambiently — a human reviewer decides before anything is removed.</> },
    ],
  },
  {
    id: 'incidents',
    num: '09',
    title: 'Incident response & disclosure',
    items: [
      { label: 'Named on-call', body: <>Our engineering team runs 24×7 on-call during placement seasons. Named security contact at <a className="link" href="mailto:security@kollez.ai">security@kollez.ai</a>.</> },
      { label: 'Notification SLA', body: <>Any incident affecting your data is disclosed within 72 hours per DPDP. Status page updated in real-time at <Link to="/status" className="link">/status</Link>.</> },
      { label: 'Post-incident report', body: <>Every material incident produces a signed post-incident report — root cause, remediation, and what changed to prevent recurrence.</> },
    ],
  },
];

/* ────────── sub-components ────────── */

function AnchorNav() {
  return (
    <nav aria-label="Security sections" className="grid gap-2 text-sm">
      <p className="eyebrow m-0 text-xs">Contents</p>
      <ol className="grid gap-1 mt-1">
        {SECTIONS.map((s) => (
          <li key={s.id}>
            <a
              href={`#${s.id}`}
              className="flex items-baseline gap-3 py-1 hover:opacity-75"
            >
              <span
                className="tabular-nums text-xs flex-shrink-0"
                style={{ color: 'rgb(var(--ink-accent))', minWidth: 22 }}
              >
                §{s.num}
              </span>
              <span>{s.title}</span>
            </a>
          </li>
        ))}
      </ol>
      <div
        className="rounded-md p-3 mt-4 text-xs"
        style={{ background: 'rgb(var(--bg-secondary))', color: 'rgb(var(--ink-secondary))' }}
      >
        <p className="m-0 mb-2 font-medium">Need this signed?</p>
        <p className="m-0 mb-2">SOC 2 report, ISO 27001 certificate and DPA available under NDA.</p>
        <a className="link text-xs" href="mailto:security@kollez.ai">Request the letters →</a>
      </div>
    </nav>
  );
}

function SectionBlock({ section }: { section: Section }) {
  return (
    <section id={section.id} className="grid gap-4 scroll-mt-24">
      <header className="grid gap-2">
        <div className="flex items-baseline gap-3">
          <span
            className="font-serif tabular-nums"
            style={{ fontSize: '1rem', color: 'rgb(var(--ink-accent))', minWidth: 36 }}
          >
            §{section.num}
          </span>
          <h2
            className="font-serif m-0"
            style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2rem)', fontWeight: 500, letterSpacing: '-0.005em' }}
          >
            {section.title}
          </h2>
        </div>
        {section.intro && (
          <p className="text-ink-secondary m-0 ml-[52px]" style={{ maxWidth: '68ch' }}>
            {section.intro}
          </p>
        )}
      </header>
      <dl className="grid gap-4 ml-[52px]">
        {section.items.map((it, i) => (
          <div
            key={i}
            className="grid gap-1 pb-4 border-b"
            style={{ borderColor: 'rgb(var(--line-tertiary))' }}
          >
            <dt className="flex items-center gap-2 font-medium">
              {it.icon && (
                <span
                  className="flex items-center justify-center rounded-md flex-shrink-0"
                  style={{
                    width: 24, height: 24,
                    background: 'rgb(var(--bg-info))',
                    color: 'rgb(var(--ink-accent))',
                  }}
                >
                  {it.icon}
                </span>
              )}
              {it.label}
            </dt>
            <dd className="text-ink-secondary m-0" style={{ maxWidth: '72ch' }}>
              {it.body}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/* ────────── page ────────── */

export function SecurityPage() {
  return (
    <>
      <PageMeta
        title="Security &amp; privacy"
        description="Kollez holds student PII, offers and audit obligations. Here's the compliance-doc-style walkthrough of how we treat that responsibly — SOC 2, ISO 27001, DPDP Act 2023, hosted in India."
        canonical="/security"
      />

      {/* HERO */}
      <section className="mx-auto max-w-page-wide px-5 lg:px-10 pt-16 lg:pt-24 pb-8 grid gap-4 max-w-3xl">
        <p className="eyebrow flex items-center gap-1.5">
          <ShieldCheck size={12} />
          Security &amp; privacy
        </p>
        <h1
          className="text-ink-primary"
          style={{
            fontFamily: 'Newsreader, Georgia, serif',
            fontSize: 'clamp(2.25rem, 5vw, 3.25rem)',
            lineHeight: 1.05,
            fontWeight: 500,
            letterSpacing: '-0.01em',
            margin: 0,
          }}
        >
          Built with the audit in mind.
        </h1>
        <p className="text-lg leading-8 text-ink-secondary m-0" style={{ maxWidth: '62ch' }}>
          Kollez runs the placement office of an Indian college — full of
          student PII, offers, and audit obligations. This page is the
          plain-English walkthrough of how we treat that responsibly, section
          by numbered section. Skim the contents; read the ones that matter
          to you.
        </p>
        <p className="helper flex items-center gap-3 flex-wrap mt-2 m-0">
          <span className="flex items-center gap-1">
            <ShieldCheck size={12} style={{ color: 'rgb(var(--ink-success))' }} />
            SOC 2 Type II
          </span>
          <span className="flex items-center gap-1">
            <BadgeCheck size={12} style={{ color: 'rgb(var(--ink-success))' }} />
            ISO 27001
          </span>
          <span className="flex items-center gap-1">
            <FileLock2 size={12} style={{ color: 'rgb(var(--ink-success))' }} />
            DPDP Act 2023
          </span>
          <span className="flex items-center gap-1">
            <MapPin size={12} style={{ color: 'rgb(var(--ink-success))' }} />
            Hosted in India
          </span>
        </p>
      </section>

      {/* DOC LAYOUT — sticky nav + numbered sections */}
      <section className="mx-auto max-w-page-wide px-5 lg:px-10 py-10 lg:py-14">
        <div className="grid gap-10 lg:grid-cols-[240px_1fr] lg:gap-16">
          <aside className="lg:sticky lg:top-24 self-start">
            <AnchorNav />
          </aside>
          <div className="grid gap-14">
            {SECTIONS.map((s) => (
              <SectionBlock key={s.id} section={s} />
            ))}

            {/* CONTACT — final block */}
            <section className="grid gap-3">
              <div className="flex items-baseline gap-3">
                <span
                  className="font-serif tabular-nums"
                  style={{ fontSize: '1rem', color: 'rgb(var(--ink-accent))', minWidth: 36 }}
                >
                  §10
                </span>
                <h2
                  className="font-serif m-0"
                  style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2rem)', fontWeight: 500, letterSpacing: '-0.005em' }}
                >
                  Contact
                </h2>
              </div>
              <div className="ml-[52px] grid gap-2 text-ink-secondary">
                <p className="m-0">
                  <b>Report a security concern:</b>{' '}
                  <a className="link" href="mailto:security@kollez.ai">security@kollez.ai</a>{' '}
                  — reply within one working day. Responsible reporters credited.
                </p>
                <p className="m-0">
                  <b>DPO / privacy questions:</b>{' '}
                  <a className="link" href="mailto:dpo@kollez.ai">dpo@kollez.ai</a>
                </p>
                <p className="m-0">
                  <b>Manage your data as a student:</b>{' '}
                  <Link className="link" to="/settings/privacy">Settings → Privacy</Link>
                </p>
                <p className="m-0">
                  <b>Signed letters (SOC 2, ISO 27001, DPA):</b> under NDA, email security@kollez.ai
                </p>
              </div>
            </section>
          </div>
        </div>
      </section>
    </>
  );
}
